import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, Download, FileText, ShieldAlert, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CustodyChain } from "@/components/tx/CustodyChain";
import { CloseForm, PickupForm, PickupQR, ReceiveForm, ScheduleForm } from "@/components/tx/StepForms";
import { STEPS, STEP_INDEX, GESTOR_STATUS, fmtDate } from "@/lib/ops";
import { clp, fileUrl, num, useApi } from "@/lib/api";
import { downloadTraceCertificate } from "@/lib/pdf";

const Stepper = ({ status }) => (
  <ol className="grid grid-cols-5 gap-2" data-testid="tx-stepper">
    {STEPS.map((s, i) => {
      const done = i <= STEP_INDEX[status];
      return (
        <li key={s.id} className="space-y-2">
          <div className={`h-1.5 rounded-full ${done ? "bg-[#00f2a1]" : "bg-slate-200"}`} />
          <p className={`text-[11px] sm:text-xs font-medium flex items-center gap-1 ${done ? "text-[#05130e]" : "text-slate-400"}`}>
            {done ? <Check className="w-3.5 h-3.5 text-[#00b37a]" /> : <span className="font-mono">0{i + 1}</span>}<span className="hidden sm:inline">{s.label}</span>
          </p>
        </li>
      );
    })}
  </ol>
);

const Party = ({ title, p }) => (
  <div className="bg-[#f4f7f6] rounded-2xl p-4 text-sm space-y-1">
    <p className="text-xs uppercase tracking-wider text-slate-500">{title}</p>
    <p className="font-semibold text-[#05130e]">{p.razon_social || p.name}</p>
    <p className="text-slate-600 font-mono text-xs">RUT {p.rut || "—"} · RETC {p.retc_id || "—"}</p>
    {p.gestor_status && p.gestor_status !== "no_declarado" && <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full border ${GESTOR_STATUS[p.gestor_status].cls}`}>{GESTOR_STATUS[p.gestor_status].label}</span>}
  </div>
);

const Waiting = ({ text }) => <p className="text-sm text-slate-500 bg-[#f4f7f6] rounded-xl p-4" data-testid="tx-waiting">{text}</p>;

const ActionPanel = ({ tx, reload }) => {
  const seller = tx.role === "generador";
  const map = {
    adjudicada: <ScheduleForm tx={tx} onDone={reload} />,
    retiro_programado: seller ? <PickupForm tx={tx} onDone={reload} /> : <PickupQR code={tx.pickup_code} />,
    retirado: seller ? <Waiting text="Esperando que la empresa receptora confirme la recepción en su planta." /> : <ReceiveForm tx={tx} onDone={reload} />,
    recibido: seller ? <CloseForm tx={tx} onDone={reload} /> : <Waiting text="Esperando que el generador registre el documento tributario de cierre." />,
  };
  if (tx.status === "cerrada") return <Closed tx={tx} />;
  const step = STEPS[STEP_INDEX[tx.status] + 1];
  return (
    <section className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8" data-testid="tx-action-panel">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00b37a] mb-1">Siguiente etapa</p>
      <h2 className="font-semibold text-xl text-[#05130e]">{step.label}</h2>
      <p className="text-sm text-slate-500 mb-6">{step.desc}</p>
      {map[tx.status]}
    </section>
  );
};

const Closed = ({ tx }) => {
  const m = tx.certificate.metrics;
  return (
    <section className="bg-[#0a1f18] rounded-3xl p-6 sm:p-8 text-white space-y-6" data-testid="tx-closed-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00f2a1]">Transacción cerrada</p>
          <h2 className="text-2xl font-bold mt-1">Certificado de Valorización y Trazabilidad</h2>
        </div>
        <Button onClick={() => downloadTraceCertificate(tx)} className="rounded-full font-semibold" data-testid="tx-download-certificate-btn"><Download className="w-4 h-4" />Descargar PDF verificable</Button>
      </div>
      <div className="grid sm:grid-cols-4 gap-4">
        {[["Toneladas valorizadas", `${num(m.tons, 2)} t`], ["CO₂ eq. mitigado", `${num(m.co2_t, 2)} t`], ["Energía evitada", `${num(m.energy_mwh, 1)} MWh`], ["Agua evitada", `${num(m.water_m3, 0)} m³`]].map(([k, v]) => (
          <div key={k} className="rounded-xl border border-[#1b3d30] p-4"><p className="text-xs text-slate-400">{k}</p><p className="font-display text-xl font-bold mt-1">{v}</p></div>
        ))}
      </div>
      <p className="text-sm text-slate-300">Verificación pública: <Link to={`/verificar/${tx.code}`} className="text-[#00f2a1] underline" data-testid="tx-verify-link">/verificar/{tx.code}</Link></p>
      {tx.closing?.file_id && <a href={fileUrl(tx.closing.file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-[#00f2a1] hover:underline"><FileText className="w-4 h-4" />{tx.closing.tipo_documento} N° {tx.closing.numero}</a>}
    </section>
  );
};

export default function Transaction() {
  const { id } = useParams();
  const api = useApi();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const load = useCallback(() => {
    api("get", `/transactions/${id}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [api, id]);
  useEffect(() => {
    load();
  }, [load]);

  if (error) return <main className="max-w-3xl mx-auto py-24 text-center" data-testid="tx-not-found">Transacción no encontrada. <Link to="/panel" className="text-[#00915f]">Volver al panel</Link></main>;
  if (!data) return <main className="max-w-5xl mx-auto px-5 py-12"><Skeleton className="h-96 rounded-3xl" /></main>;
  const { tx, events } = data;

  return (
    <main className="max-w-5xl mx-auto px-5 lg:px-8 py-10 space-y-6" data-testid="transaction-page">
      <Link to="/panel" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#05130e]" data-testid="tx-back-link"><ArrowLeft className="w-4 h-4" />Mi panel</Link>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00b37a]">Transacción {tx.code} · {tx.role === "generador" ? "Eres el generador" : "Eres la empresa receptora"}</p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#05130e] mt-1" data-testid="tx-title">{tx.lot.title}</h1>
          <p className="text-sm text-slate-600 mt-1">{num(tx.lot.weight_tons, 2)} t · {tx.lot.comuna}, {tx.lot.region} · {tx.mode === "donacion" ? "Donación" : clp(tx.amount)} · adjudicada {fmtDate(tx.created_at)}</p>
        </div>
        <span className={`flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border ${tx.rep_valid ? "bg-[#00f2a1]/15 text-[#007a50] border-[#00f2a1]/40" : "bg-amber-50 text-amber-700 border-amber-200"}`} data-testid="tx-rep-badge">
          {tx.rep_valid ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}{tx.rep_valid ? "Válida para Ley REP (gestor declarado)" : "Receptor sin declaración de gestor vigente"}
        </span>
      </div>
      <Stepper status={tx.status} />
      <div className="grid sm:grid-cols-2 gap-4"><Party title="Generador" p={tx.seller} /><Party title="Destinatario / Transformador" p={tx.buyer} /></div>
      {tx.schedule && <p className="text-sm text-slate-600 bg-white border border-slate-200 rounded-2xl p-4" data-testid="tx-schedule-summary">Orden de Retiro: {tx.schedule.pickup_date} {tx.schedule.pickup_time} · {tx.schedule.address} · {num(tx.schedule.volume_tons, 2)} t reservadas{tx.schedule.transporter && ` · Transportista ${tx.schedule.transporter}`}{tx.schedule.plate && ` · Patente ${tx.schedule.plate}`}</p>}
      <ActionPanel tx={tx} reload={load} />
      <CustodyChain events={events} />
    </main>
  );
}
